import * as React from 'react';
import { FC, useMemo } from 'react';

import {
  capitalizeFeeName,
  getFeesByGroup,
  getStandardizedName,
} from 'features/cart/components/totals/utils';
import { getCartTotalCentsWithServerOrderSurcharges } from 'features/cart/utils/server-order-surcharges';
import { StyleSheet } from 'react-native';

import { IDiscount, IFee } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import { Box, Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { primitive, spacing } from 'styles/constants';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import { getDeliveryFees } from 'utils/order';
import { geographicalFeeLabelIdMap, isMappedGeographicalFeeState } from 'utils/order';

import { MinimalAccountOrder } from '../types';

import getRewardsDiscount from './get-rewards-discount';

const ReceiptDetail = ({ children, total }: React.PropsWithChildren<{ total?: boolean }>) => (
  <Box style={[styles.receiptDetail, total && styles.receiptDetailTotal]} accessible focusable>
    {children}
  </Box>
);

const ReceiptDetailsText = ({
  total,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & { total?: boolean }) => (
  <Typography variant={total ? 'copyOne' : 'copyTwo'} bold={total} {...props} />
);

const mapMinimalDiscountsToDiscounts = (
  discounts?: ReadonlyArray<{ name?: string | null; value?: number | null } | null> | null
): IDiscount[] =>
  (discounts ?? []).flatMap(discount =>
    discount?.name != null && discount?.value != null
      ? [
          {
            name: discount.name as IDiscount['name'],
            value: discount.value,
          },
        ]
      : []
  );

const mapMinimalFeesToFees = (
  fees?: ReadonlyArray<{
    name?: string | null;
    priceCents?: number | null;
    quantity?: number | null;
    totalCents?: number | null;
  } | null> | null
): IFee[] =>
  (fees ?? []).flatMap(fee =>
    fee?.name != null && fee?.priceCents != null && fee?.quantity != null && fee?.totalCents != null
      ? [
          {
            name: fee.name,
            priceCents: fee.priceCents,
            quantity: fee.quantity,
            totalCents: fee.totalCents,
          },
        ]
      : []
  );

const MinimalReceiptDetails: FC<React.PropsWithChildren<{ order: MinimalAccountOrder }>> = ({
  order,
}) => {
  const { formatCurrencyForLocale: currencyFormatter } = useUIContext();
  const { delivery } = order;
  const { subTotalCents, taxCents } = order.cart;

  const {
    feeCents: deliveryFeeCents,
    isFreeDelivery,
    deliveryFeeDiscountCents,
    geographicalFeeCents,
    serviceFeeCents,
    smallCartFeeCents,
  } = getDeliveryFees({
    delivery: delivery
      ? {
          dropoff: {
            state: delivery.dropoff?.state ?? '',
          },
          feeCents: delivery.feeCents,
          tipCents: delivery.tipCents ?? 0,
          feeDiscountCents: delivery.feeDiscountCents ?? 0,
          serviceFeeCents: delivery.serviceFeeCents ?? 0,
          smallCartFeeCents: delivery.smallCartFeeCents ?? 0,
          geographicalFeeCents: delivery.geographicalFeeCents ?? 0,
          status: delivery.status!,
        }
      : null,
  });

  const enableFeesOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_FEES_ON_ORDER_RECEIPT);
  const enableDiscountsOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_DISCOUNTS_ON_ORDER_RECEIPT);
  const enablePosOrderSurchargesAsFees = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_POS_ORDER_SURCHARGES_AS_FEES)
  );

  const discounts = useMemo(() => mapMinimalDiscountsToDiscounts(order.cart.discounts), [
    order.cart.discounts,
  ]);
  const dropoffState = delivery?.dropoff?.state;

  const geographicalFeeLabelId = (isMappedGeographicalFeeState(dropoffState)
    ? geographicalFeeLabelIdMap[dropoffState]
    : 'geographicalFee') as TranslationKey;

  const rewardsDiscountCents = useMemo(() => getRewardsDiscount({ discounts }), [discounts]);

  const otherDiscountAmount = computeOtherDiscountAmount(discounts);
  const fees = useMemo(() => mapMinimalFeesToFees(order.cart.fees), [order.cart.fees]);
  const itemFees = getFeesByGroup(fees);
  const shouldRenderCartFees = enableFeesOnOrderReceipt || enablePosOrderSurchargesAsFees;
  const adjustedTotalCents = getCartTotalCentsWithServerOrderSurcharges({
    cart: order.cart,
    enablePosOrderSurchargesAsFees,
  });

  return (
    <Box>
      {order.delivery && (
        <Box>
          <ReceiptDetail>
            <ReceiptDetailsText>{t('deliveryFee')}:</ReceiptDetailsText>
            {isFreeDelivery ? (
              <Typography style={styles.feeDeliveryItem}>
                {t('free')}
                &nbsp;
                <Typography style={styles.discount}>
                  <Currency amount={deliveryFeeDiscountCents} />
                </Typography>
              </Typography>
            ) : (
              <ReceiptDetailsText>{currencyFormatter(deliveryFeeCents)}</ReceiptDetailsText>
            )}
          </ReceiptDetail>
          {geographicalFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t(geographicalFeeLabelId)}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(geographicalFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {serviceFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t('serviceFee')}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(serviceFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {smallCartFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t('smallCartFee')}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(smallCartFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          <ReceiptDetail>
            <ReceiptDetailsText>{t('tip')}:</ReceiptDetailsText>
            <ReceiptDetailsText>
              {currencyFormatter(order.delivery.tipCents ?? 0)}
            </ReceiptDetailsText>
          </ReceiptDetail>
        </Box>
      )}
      {enableDiscountsOnOrderReceipt && !!otherDiscountAmount && (
        <ReceiptDetail>
          <ReceiptDetailsText>{t('otherDiscounts')}</ReceiptDetailsText>
          <ReceiptDetailsText>{` - ${currencyFormatter(otherDiscountAmount)}`}</ReceiptDetailsText>
        </ReceiptDetail>
      )}
      {!!rewardsDiscountCents && (
        <ReceiptDetail>
          <ReceiptDetailsText color={primitive.$crunchyGreen}>
            {t('rewardSavings')}
          </ReceiptDetailsText>
          <ReceiptDetailsText color={primitive.$crunchyGreen}>
            {` - ${currencyFormatter(rewardsDiscountCents)}`}
          </ReceiptDetailsText>
        </ReceiptDetail>
      )}
      <ReceiptDetail>
        <ReceiptDetailsText>{t('subtotal')}</ReceiptDetailsText>
        <ReceiptDetailsText>{currencyFormatter(subTotalCents ?? 0)}</ReceiptDetailsText>
      </ReceiptDetail>
      {shouldRenderCartFees &&
        Object.entries(itemFees).map(([name, entryCents]) => {
          const standardizedName = getStandardizedName({ name });
          const feeTranslationKey = `${standardizedName}Fee` as TranslationKey;
          return (
            <ReceiptDetail key={name}>
              <ReceiptDetailsText>
                {capitalizeFeeName(t(feeTranslationKey, name))}
              </ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(entryCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          );
        })}
      <ReceiptDetail>
        <ReceiptDetailsText>{t('tax')}</ReceiptDetailsText>
        <ReceiptDetailsText>{currencyFormatter(taxCents ?? 0)}</ReceiptDetailsText>
      </ReceiptDetail>
      <ReceiptDetail total>
        <ReceiptDetailsText total>{t('total')}</ReceiptDetailsText>
        <ReceiptDetailsText total>{currencyFormatter(adjustedTotalCents)}</ReceiptDetailsText>
      </ReceiptDetail>
    </Box>
  );
};

export default MinimalReceiptDetails;

const styles = StyleSheet.create({
  discount: {
    textDecorationLine: 'line-through',
  },
  feeDeliveryItem: {
    fontFamily: primitive.fontFamily.$headers,
    margin: 0,
  },
  receiptDetail: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  receiptDetailTotal: {
    marginVertical: spacing.$3,
  },
});
